/**
 * Centralized P&L calculation utilities.
 * 
 * ALL P&L logic should use these functions to avoid
 * duplicate/inconsistent calculations across components.
 */

/**
 * Tailwind text classes for a P&L value's sign.
 * Variants are full class literals (not interpolated) so the Tailwind JIT
 * scanner keeps them in the build.
 */
export const pnlColor = (v: number): string => (v >= 0 ? 'text-up' : 'text-down');
// Faded variants stay literal green/red: the --pnl-up/--pnl-down tokens bake in their own
// value, and Tailwind opacity modifiers (e.g. text-up/60) don't apply to var()-backed colors.
// In v1's dark themes these match the tokens exactly; the deferred light themes will need
// rgb-channel P&L tokens to make the faded variants theme-reactive too.
export const pnlColor60 = (v: number): string => (v >= 0 ? 'text-green-400/60' : 'text-red-400/60');
export const pnlColor50 = (v: number): string => (v >= 0 ? 'text-green-400/50' : 'text-red-400/50');
export const pnlColor30 = (v: number): string => (v >= 0 ? 'text-green-400/30' : 'text-red-400/30');

export interface PnLBreakdown {
  /** Option leg P&L: (entry_premium - current_premium) × shares */
  optionPnl: number;
  /** Stock leg P&L (CC only): (current_stock - entry_stock) × shares */
  stockPnl: number;
  /** Total P&L: optionPnl + stockPnl */
  totalPnl: number;
  /** P&L percent on capital at risk (strike × shares for CSP, stock cost for CC); null when the
   *  backend served none — shown as "—", never as 0 */
  totalPnlPct: number | null;
  /** P&L percent on premium received (option-only view) */
  premiumPnlPct: number;
  /** Whether this is a CC position with stock data */
  hasStockLeg: boolean;
}

